import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, FilePdf, Circle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Markdown } from "../components/Markdown";

const DOCS = [
  { key: "caregiver_guide", title: "Caregiver Shift Guide", audience: "PRIMARY" },
  { key: "risk_dashboard", title: "Risk Dashboard", audience: "INTERNAL" },
  { key: "pattern_analysis", title: "Pattern Analysis", audience: "INTERNAL" },
  { key: "hidden_risks", title: "Hidden Risks", audience: "INTERNAL" },
  { key: "strengths_goals", title: "Strengths & Goals", audience: "ALL" },
  { key: "escalation_tree", title: "Escalation Tree", audience: "CAREGIVER" },
  { key: "communication_guide", title: "Communication Guide", audience: "CAREGIVER" },
  { key: "care_plan_narrative", title: "Care Plan Narrative", audience: "FORMAL" },
  { key: "watch_list", title: "Watch List & Medications", audience: "CAREGIVER" },
  { key: "equipment_environment", title: "Equipment & Environment", audience: "INTERNAL" },
];

const AUDIENCE_STYLES = {
  PRIMARY: "bg-primary text-primary-foreground",
  CAREGIVER: "bg-primary/10 text-primary",
  INTERNAL: "bg-stone-200 text-stone-600",
  ALL: "bg-[#27AE60]/10 text-[#1e8449]",
  FORMAL: "bg-accent text-accent-foreground",
};

const RiskDashboard = ({ data }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="risk-dashboard">
    {[
      { label: "High Risk", items: data?.high || [], cls: "risk-high", tid: "risk-high-list" },
      { label: "Moderate Risk", items: data?.moderate || [], cls: "risk-moderate", tid: "risk-moderate-list" },
      { label: "Monitored", items: data?.monitored || [], cls: "risk-monitored", tid: "risk-monitored-list" },
    ].map(({ label, items, cls, tid }) => (
      <div key={label} className={`rounded-lg border p-5 ${cls}`} data-testid={tid}>
        <p className="font-heading text-sm font-semibold uppercase tracking-wide">{label}</p>
        <ul className="mt-3 space-y-2.5">
          {items.length === 0 && <li className="text-sm opacity-70">None identified</li>}
          {items.map((item, i) => (
            <li key={i} className="flex gap-2 text-sm" data-testid={`${tid}-item-${i}`}>
              <Circle size={8} weight="fill" className="mt-1.5 shrink-0" /> {item}
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

export default function DocumentsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [active, setActive] = useState("caregiver_guide");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get(`/clients/${id}/documents`)
      .then((r) => setPayload(r.data))
      .catch((e) => { toast.error(formatApiError(e)); navigate(`/clients/${id}`); });
  }, [id, navigate]);

  if (!payload) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  const doc = DOCS.find((d) => d.key === active);
  const content = payload.documents[active];

  const copy = async () => {
    let text = "";
    if (active === "risk_dashboard" && content && typeof content === "object") {
      text = ["HIGH RISK", ...(content.high || []).map((i) => `- ${i}`), "",
        "MODERATE RISK", ...(content.moderate || []).map((i) => `- ${i}`), "",
        "MONITORED", ...(content.monitored || []).map((i) => `- ${i}`)].join("\n");
    } else {
      text = String(content || "");
    }
    await navigator.clipboard.writeText(`${doc.title}\nClient: ${payload.client_name || payload.client_code}\n\n${text}`);
    toast.success("Copied to clipboard");
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/clients/${id}/documents/${active}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(payload.client_name || payload.client_code).replace(/\s+/g, "_")}_${active}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="documents-page">
      <button onClick={() => navigate(`/clients/${id}`)} className="mb-4 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="back-to-client">
        <ArrowLeft size={14} /> Back to assessments
      </button>
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Generated Care Plan</h1>
        <p className="mt-1 text-sm text-stone-500">
          Client <span className="rounded bg-accent px-1.5 py-0.5 text-xs font-semibold text-accent-foreground" data-testid="docs-client-code">{payload.client_name || payload.client_code}</span>
          {" "}· 10 documents · Generated {String(payload.generated_at || "").slice(0, 16).replace("T", " ")}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[280px_1fr]">
        <nav className="space-y-1.5 lg:sticky lg:top-24 lg:self-start" data-testid="documents-nav">
          {DOCS.map((d, i) => (
            <button key={d.key} onClick={() => setActive(d.key)} data-testid={`doc-tab-${d.key}`}
              className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition-all hover:-translate-y-0.5 ${
                active === d.key ? "border-primary bg-white shadow-sm" : "border-stone-200 bg-white/60 hover:bg-white"
              }`}>
              <span className={`text-sm font-medium ${active === d.key ? "text-primary" : "text-stone-700"}`}>
                <span className="mr-2 text-xs text-stone-400">{i + 1}</span>{d.title}
              </span>
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${AUDIENCE_STYLES[d.audience]}`}>{d.audience}</span>
            </button>
          ))}
        </nav>

        <div className="rounded-lg border border-stone-200 bg-white p-8 shadow-sm" data-testid="document-viewer">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-stone-200 pb-6">
            <div>
              <Badge variant="outline" className="mb-2 border-stone-300 text-stone-500">{doc.audience} DOCUMENT</Badge>
              <h2 className="font-heading text-2xl font-semibold tracking-tight text-stone-900" data-testid="document-title">{doc.title}</h2>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={copy} className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="copy-doc-button">
                <Copy size={15} className="mr-1.5" /> Copy
              </Button>
              <Button variant="outline" size="sm" onClick={downloadPdf} disabled={downloading}
                className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="download-pdf-button">
                <FilePdf size={15} className="mr-1.5" /> {downloading ? "Downloading..." : "PDF"}
              </Button>
            </div>
          </div>
          {active === "risk_dashboard" ? <RiskDashboard data={content} /> : <Markdown text={content} />}
        </div>
      </div>
    </div>
  );
}
